import { FieldType, TextStyle } from '@coast/schemas';
import moment from 'moment';
import { ReactElement, ReactNode } from 'react';
import { View } from '@shared/components/View';
import { CardLayoutContext, CardRenderContext } from '@shared/components/blocks/contexts/render';
import { Reminder, TodoItem } from '@shared/components/blocks/types';
import { getTypoComponent } from '@shared/components/blocks/utils';
import { MBImage } from '@shared/components/image';
import { grid } from '@shared/styles';
import {
  formatDateTimeWithYear,
  formatDateWithYear,
  formatRelativeTime,
  formatTime,
} from '@shared/util/date';
import { i18n } from '@shared/util/i18n';
import { AddressContent } from './inputAddress/content';
import { GeolocationContent } from './inputGeolocation/content';
import { formatNumber } from './inputNumber/util';
import { toString as recurrenceToString } from './inputRecurrence/utils';
import { format as formatReminder } from './inputReminder/hooks';

export function defaultFieldPresenter(
  value: any,
  fieldType: FieldType | undefined,
  textStyle: TextStyle,
  textColor: string,
): ReactElement {
  const TypoComponent = getTypoComponent(textStyle);
  let content: ReactNode;
  switch (fieldType) {
    case 'cards':
      content = i18n('card.blocks.cards.value', { count: value?.length ?? 0 });
      break;
    case 'text':
    case 'textarea':
      content = value;
      break;
    case 'geolocation':
      content = <GeolocationContent geolocation={value} />;
      break;
    case 'address':
      content = <AddressContent textStyle={textStyle} address={value} />;
      break;
    case 'date':
      content = formatDateWithYear(value);
      break;
    case 'time':
      content = formatTime(value);
      break;
    case 'relativeTime':
      content = formatRelativeTime(value);
      break;
    case 'float':
    case 'percent':
    case 'currency':
      content = formatNumber(value, fieldType);
      break;
    case 'datetime':
      content = formatDateTimeWithYear(value);
      break;
    case 'recurrence':
      content = value ? recurrenceToString(value) : '';
      break;
    case 'toggle':
      // TODO(fant): how do we display toggle fields?
      content = value ? '✅' : '🔴';
      break;
    case 'reminder':
      content = value.map((r: Reminder) => formatReminder(moment.duration(r.duration))).join(', ');
      break;
    case 'todo':
      const items: TodoItem[] = value;
      content = i18n('card.blocks.todo.summary', {
        completedCount: items.filter(item => item.status === 'COMPLETED').length,
        count: items.length,
      });
      break;
    case 'signature':
      content = (
        <View style={{ height: grid(12) }}>
          <MBImage source={{ uri: value.url }} fill />
        </View>
      );
      break;
    default:
      if (typeof value === 'object') {
        content = JSON.stringify(value);
      } else {
        content = value;
      }
      break;
  }
  return (
    <CardLayoutContext.Consumer>
      {layoutMode => (
        <CardRenderContext.Consumer>
          {renderContext => (
            <View centerVertically>
              {typeof content === 'string' ? (
                <TypoComponent
                  allowFontScaling={false}
                  color={textColor}
                  ellipsizeMode="tail"
                  numberOfLines={(() => {
                    if (['agenda', 'calendar'].includes(renderContext) || layoutMode === 'row') {
                      return 1;
                    }
                  })()}
                >
                  {content}
                </TypoComponent>
              ) : (
                content
              )}
            </View>
          )}
        </CardRenderContext.Consumer>
      )}
    </CardLayoutContext.Consumer>
  );
}
